import { TableHead } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BigLoader from '../common/BigLoader';
import { getTranslation } from '../models/Translation';
import ScrollableTable from '../table/ScrollableTable';
import TableHeadCell, { QueryOrder } from '../table/TableHeadCell';
import useFetch from '../hooks/useFetch';
import UserContext from '../contexts/UserContext';
import { hasRole } from '../models/User';
import { RoleType } from '../models/Role';
import { getRatingStatistics } from '../config/apiRoutes';
import { MenuItemStats, parseMenuItemStats } from '../models/Statistics';
import SnackbarContext from '../contexts/SnackbarContext';
import MenuStatisticTableRow from './MenuStatisticTableRow';

type Props = {}

const MenuLikeStatisticTable = (props: Props) => {
    const { t } = useTranslation();
    const ratingCaller = useFetch();
    const { selectedRestaurant, user, hasFeature } = useContext(UserContext);
    const hasAccess = hasRole(user, RoleType.OWNER) && hasFeature("ANALYTICS_INSIGHTS");
    const [menuItemRatings, setMenuItemRatings] = useState<MenuItemStats[]>([]);
    const snackbar = useContext(SnackbarContext);
    const [orderBy, setOrderBy] = useState<QueryOrder | null>({ orderBy: "likeCount", orderDir: "desc" });

    const displayedItemRatings = useMemo(() => {
        switch (orderBy?.orderBy) {
            case "name":
                return [...menuItemRatings].sort(compareByItemName);
            case "likeCount":
                return [...menuItemRatings].sort(compareByLikeCount);
            case "dislikeCount":
                return [...menuItemRatings].sort(compareByDislikeCount);
            case "weeklyTrend":
                return [...menuItemRatings].sort(compareByWeeklyTrend);
            case "monthlyTrend":
                return [...menuItemRatings].sort(compareByMonthlyTrend);
            default:
                return [...menuItemRatings].sort(compareByLikeCount);
        }
    }, [menuItemRatings, orderBy]);

    function compareByItemName(a: MenuItemStats, b: MenuItemStats) {
        const titleA = getTranslation(a.translations).title.toLowerCase();
        const titleB = getTranslation(b.translations).title.toLowerCase();
        if (orderBy?.orderDir === 'asc') {
            return titleA.localeCompare(titleB);
        }
        return titleB.localeCompare(titleA);
    }

    function compareByLikeCount(a: MenuItemStats, b: MenuItemStats) {
        if (orderBy?.orderDir === 'asc') {
            return a.likeCount - b.likeCount;
        }
        return b.likeCount - a.likeCount;
    }

    function compareByDislikeCount(a: MenuItemStats, b: MenuItemStats) {
        if (orderBy?.orderDir === 'asc') {
            return a.dislikeCount - b.dislikeCount;
        }
        return b.dislikeCount - a.dislikeCount;
    }

    function compareByWeeklyTrend(a: MenuItemStats, b: MenuItemStats) {
        const netA = a.trend7.likes - a.trend7.dislikes;
        const netB = b.trend7.likes - b.trend7.dislikes;
        if (orderBy?.orderDir === 'asc') {
            return netA - netB;
        }
        return netB - netA;
    }

    function compareByMonthlyTrend(a: MenuItemStats, b: MenuItemStats) {
        const netA = a.trend30.likes - a.trend30.dislikes;
        const netB = b.trend30.likes - b.trend30.dislikes;
        if (orderBy?.orderDir === 'asc') {
            return netA - netB;
        }
        return netB - netA;
    }

    useEffect(() => {
        if (selectedRestaurant && hasAccess) {
            fetchRatingStatistics();
        }
    }, [selectedRestaurant]);

    async function fetchRatingStatistics() {
        const url = getRatingStatistics.replace(":restaurantShortName", selectedRestaurant?.restaurantShortName || "");
        const response = await ratingCaller.call<undefined, any>("GET", url);
        if (response.ok && response.data) {
            setMenuItemRatings(parseMenuItemStats(response.data));
        } else {
            snackbar.showMessage(t("statistics.error_fetching_rating_statistics"), "error");
        }
    }

    return (
        <ScrollableTable>
            <TableHead>
                <tr className="h-[56px] bg-neutral-deep">
                    <TableHeadCell className="w-1/3" orderKey="name" order={orderBy} setOrder={setOrderBy} showOrderIcon={true}>
                        {t("statistics.menu_item")}
                    </TableHeadCell>
                    <TableHeadCell align="right" orderKey="likeCount" order={orderBy} setOrder={setOrderBy} showOrderIcon={true}>
                        {t("statistics.likes")}
                    </TableHeadCell>
                    <TableHeadCell align="right" orderKey="dislikeCount" order={orderBy} setOrder={setOrderBy} showOrderIcon={true}>
                        {t("statistics.dislikes")}
                    </TableHeadCell>
                    <TableHeadCell align="right" orderKey="weeklyTrend" order={orderBy} setOrder={setOrderBy} showOrderIcon={true}>
                        {t("statistics.rating_trends_last_7_days")}
                    </TableHeadCell>
                    <TableHeadCell align="right" orderKey="monthlyTrend" order={orderBy} setOrder={setOrderBy} showOrderIcon={true}>
                        {t("statistics.rating_trends_last_30_days")}
                    </TableHeadCell>
                </tr>
            </TableHead>
            <tbody>
                {ratingCaller.loading ? (
                    <tr>
                        <td colSpan={5}>
                            <BigLoader />
                        </td>
                    </tr>
                ) : (displayedItemRatings.map((row, index) => <MenuStatisticTableRow key={`item-stat-row-${row.menuItemId}`} itemStats={row} index={index} />))}
            </tbody>
        </ScrollableTable >
    )
}

export default MenuLikeStatisticTable